import { DEVELOPER_PLATFORM_PATH } from '@mysaiva/core/developer-platform'
import type { IconName } from '@mysaiva/core/icons'
import { resourcesFromOpenApi } from './docs/openapi/fromOpenApi'
import { overviewFromOpenApi } from './docs/openapi/overviewFromOpenApi'
import type { OpenApiSpec } from './docs/openapi/openapiSchema.types'
import openapiSnapshot from './docs/openapi/openapiSnapshot.json'

/**
 * Single registry of every workspace page. The sidebar, the header
 * breadcrumb, the global search and the route table all read from it so a
 * page can never be reachable from one place and missing from another.
 */
export type WorkspaceSectionId = 'starting' | 'develop' | 'documentation'

export type WorkspacePageId =
  | 'dashboard'
  | 'api-keys'
  | 'api-overview'
  | 'api-openapi-preview'

/** Sub-page of a documentation section, rendered as a sidebar sub-item. */
export interface WorkspaceSubPage {
  /** Unique across the whole registry, e.g. `api-reference:authentication`. */
  id: string
  /** Path segment under the parent page, e.g. `authentication`. */
  slug: string
  /** i18n key for the label (sidebar, breadcrumb, search). Ignored when `label` is set. */
  labelKey?: string
  /**
   * Plain-text label, for sub-pages generated from content that isn't
   * hand-translated (e.g. the OpenAPI preview's endpoint list). Takes
   * precedence over `labelKey` — use `subPageLabel()` to read either.
   */
  label?: string
}

/** Sub-page label: `label` (generated content) wins over the translated `labelKey`. */
export function subPageLabel(subPage: WorkspaceSubPage, t: (key: string) => string): string {
  if (subPage.label) return subPage.label
  return subPage.labelKey ? t(subPage.labelKey) : subPage.id
}

export interface WorkspacePage {
  id: WorkspacePageId
  section: WorkspaceSectionId
  /** Path relative to the workspace root, without leading slash. */
  path: string
  /** i18n key for the label (sidebar, breadcrumb, search). */
  labelKey: string
  /**
   * Sidebar icon. The design keeps every page entry text-only (the dashboard
   * icon went in the second design review round, SAIV-11493); only the
   * sidebar's own Support entry draws one.
   */
  icon?: IconName
  /** Documentation sub-pages (Platform-11). The first one is the section's landing page. */
  children?: WorkspaceSubPage[]
}

export const WORKSPACE_SECTIONS: { id: WorkspaceSectionId; titleKey: string }[] = [
  { id: 'starting', titleKey: 'developerPlatform.sections.starting' },
  { id: 'develop', titleKey: 'developerPlatform.sections.develop' },
  { id: 'documentation', titleKey: 'developerPlatform.sections.documentation' }
]

/**
 * Sidebar entries for the OpenAPI preview: derived from the bundled snapshot
 * (`docs/openapi/openapiSnapshot.json`), not the live fetch, so the sidebar
 * structure is available synchronously like every other page. The preview
 * page itself still renders live (or snapshot-fallback) content per
 * endpoint — only this list of endpoints is fixed at build time.
 */
const openApiPreviewChildren: WorkspaceSubPage[] = resourcesFromOpenApi(
  openapiSnapshot as OpenApiSpec
).map(resource => ({
  id: `api-openapi-preview:${resource.id}`,
  slug: resource.id,
  label: resource.requestTitle
}))

/**
 * Sidebar entries for the API Overview: one per `##` section of the overview
 * the backend publishes as `info.description` (rate limiting, error codes,
 * …), taken from the same snapshot so they are available synchronously. The
 * page renders the live document's sections.
 */
const overviewChildren: WorkspaceSubPage[] = overviewFromOpenApi(
  openapiSnapshot as OpenApiSpec
).map(section => ({
  id: `api-overview:${section.id}`,
  slug: section.id,
  label: section.title
}))

export const WORKSPACE_PAGES: WorkspacePage[] = [
  {
    id: 'dashboard',
    section: 'starting',
    path: '',
    labelKey: 'developerPlatform.pages.dashboard'
  },
  {
    id: 'api-keys',
    section: 'develop',
    path: 'api-keys',
    labelKey: 'developerPlatform.pages.apiKeys'
  },
  {
    id: 'api-overview',
    section: 'documentation',
    path: 'docs/overview',
    labelKey: 'developerPlatform.pages.apiOverview',
    children: overviewChildren
  },
  {
    id: 'api-openapi-preview',
    section: 'documentation',
    path: 'docs/openapi',
    labelKey: 'developerPlatform.pages.apiOpenapiPreview',
    children: openApiPreviewChildren
  }
]

/** Absolute path of a workspace page, e.g. `/integrations/api-keys`. */
export function workspacePath(pageOrId: WorkspacePage | WorkspacePageId): string {
  const page = typeof pageOrId === 'string' ? getWorkspacePage(pageOrId) : pageOrId
  return page.path ? `${DEVELOPER_PLATFORM_PATH}/${page.path}` : DEVELOPER_PLATFORM_PATH
}

/** Absolute path of a documentation sub-page, e.g. `/integrations/docs/openapi/list-patients`. */
export function subPagePath(
  page: WorkspacePage | WorkspacePageId,
  sub: WorkspaceSubPage | string
): string {
  const slug = typeof sub === 'string' ? sub : sub.slug
  return `${workspacePath(page)}/${slug}`
}

export function getWorkspacePage(id: WorkspacePageId): WorkspacePage {
  const page = WORKSPACE_PAGES.find(p => p.id === id)
  if (!page) throw new Error(`Unknown workspace page: ${id}`)
  return page
}

export function getWorkspaceSubPage(
  pageId: WorkspacePageId,
  slug: string
): WorkspaceSubPage | undefined {
  return getWorkspacePage(pageId).children?.find(child => child.slug === slug)
}

export interface WorkspaceLocation {
  page: WorkspacePage
  /** Set when the pathname points at a documentation sub-page. */
  subPage?: WorkspaceSubPage
  /** Remaining path after the page (or sub-page), e.g. a key id. */
  rest: string
}

/**
 * Resolve which page (and sub-page) is active for a pathname. Longest
 * matching path wins so `/integrations/api-keys/123` maps to `api-keys`
 * and `/integrations` alone maps to `dashboard`.
 */
export function resolveWorkspaceLocation(pathname: string): WorkspaceLocation {
  const relative = pathname
    .replace(new RegExp(`^${DEVELOPER_PLATFORM_PATH}`), '')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')

  const page =
    [...WORKSPACE_PAGES]
      .filter(candidate => candidate.path !== '')
      .sort((a, b) => b.path.length - a.path.length)
      .find(
        candidate => relative === candidate.path || relative.startsWith(`${candidate.path}/`)
      ) ?? getWorkspacePage('dashboard')

  const rest = page.path ? relative.slice(page.path.length).replace(/^\/+/, '') : relative
  const [firstSegment, ...tail] = rest.split('/')
  const subPage = page.children?.find(child => child.slug === firstSegment)

  return { page, subPage, rest: subPage ? tail.join('/') : rest }
}

/** @deprecated Use `resolveWorkspaceLocation` when the sub-page matters. */
export function resolveWorkspacePage(pathname: string): WorkspacePage {
  return resolveWorkspaceLocation(pathname).page
}

/** Sidebar item id for a location: the sub-page when there is one, else the page. */
export function activeSidebarItemId(location: WorkspaceLocation): string {
  return location.subPage?.id ?? location.page.id
}
